import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAdminSession } from "@/hooks/useAdminSession";
import {
  listAllTeams,
  getTeamDetail,
  createAdminTeam,
  suspendTeam,
  reactivateTeam,
  inviteTeamMember,
  cancelTeamInvitation,
  suspendTeamMember,
  reactivateTeamMember,
  removeTeamMember,
  type ListAllTeamsParams,
  type CreateAdminTeamInput,
  type InvitableRole,
} from "./teams";

export const teamQk = {
  list: (params: ListAllTeamsParams) => ["admin", "teams", "list", params] as const,
  detail: (teamId: string) => ["admin", "teams", teamId] as const,
};

export function useAdminTeams(params: ListAllTeamsParams) {
  const enabled = useAdminSession();
  return useQuery({
    queryKey: teamQk.list(params),
    enabled,
    queryFn: () => listAllTeams(params),
  });
}

export function useAdminTeamDetail(teamId: string | undefined) {
  const enabled = useAdminSession();
  return useQuery({
    queryKey: teamQk.detail(teamId ?? ""),
    enabled: enabled && !!teamId,
    queryFn: () => getTeamDetail(teamId!),
  });
}

export function useCreateAdminTeam() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateAdminTeamInput) => createAdminTeam(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "teams"] });
    },
  });
}

export function useSuspendTeam() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ teamId, reason }: { teamId: string; reason: string }) =>
      suspendTeam(teamId, reason),
    onSuccess: (_, { teamId }) => {
      qc.invalidateQueries({ queryKey: ["admin", "teams"] });
      qc.invalidateQueries({ queryKey: teamQk.detail(teamId) });
    },
  });
}

export function useReactivateTeam() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (teamId: string) => reactivateTeam(teamId),
    onSuccess: (_, teamId) => {
      qc.invalidateQueries({ queryKey: ["admin", "teams"] });
      qc.invalidateQueries({ queryKey: teamQk.detail(teamId) });
    },
  });
}

// ── Member management (operator levers) ─────────────────────────────────────

function useTeamDetailMutation<TArgs extends { teamId: string }, TResult>(
  fn: (args: TArgs) => Promise<TResult>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (_result: TResult, { teamId }: TArgs) => {
      qc.invalidateQueries({ queryKey: ["admin", "teams"] });
      qc.invalidateQueries({ queryKey: teamQk.detail(teamId) });
    },
  });
}

export function useInviteTeamMember() {
  return useTeamDetailMutation(
    ({ teamId, phone, role }: { teamId: string; phone: string; role: InvitableRole }) =>
      inviteTeamMember(teamId, { phone, role }),
  );
}

export function useCancelTeamInvitation() {
  return useTeamDetailMutation(
    ({ teamId, invitationId }: { teamId: string; invitationId: string }) =>
      cancelTeamInvitation(teamId, invitationId),
  );
}

export function useSuspendTeamMember() {
  return useTeamDetailMutation(({ teamId, userId }: { teamId: string; userId: string }) =>
    suspendTeamMember(teamId, userId),
  );
}

export function useReactivateTeamMember() {
  return useTeamDetailMutation(({ teamId, userId }: { teamId: string; userId: string }) =>
    reactivateTeamMember(teamId, userId),
  );
}

export function useRemoveTeamMember() {
  return useTeamDetailMutation(({ teamId, userId }: { teamId: string; userId: string }) =>
    removeTeamMember(teamId, userId),
  );
}